import { Component, computed, DestroyRef, inject, input, output, signal, viewChild } from '@angular/core';
import { AccordionModule } from '@openng/optimus-ui/accordion';
import {
  DsmAddendumOptionsPayload,
  DsmDealOption,
  DsmDealOptionProduct
} from '../../../models/dsm-deal-options-view.models';
import { ConfirmDialogModule } from '@openng/optimus-ui/confirmdialog';
import { ConfirmationService } from '@openng/optimus-ui/api';
import { DsmDealOptionsService } from '../../../services/dsm-deal-options.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs';
import { ToggleSwitchModule } from '@openng/optimus-ui/toggleswitch';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CurrencyFormatPipe, ImagePreview, ImagePreviewItem } from '@zayan-erp/shared';
import { DsmApsDealContextService } from '../../../services/dsm-aps-deal-context.service';
import { Menu, MenuModule } from '@openng/optimus-ui/menu';
import { ButtonModule } from '@openng/optimus-ui/button';
import { MenuItem } from '@openng/optimus-ui/api';
import { AbilityService } from '@zayan-erp/shared';
import { TagModule } from '@openng/optimus-ui/tag';
import { DsmLotIncentiveService } from '../../../services/dsm-lot-incentive.service';
import { DsmPackageService } from '../../../services/dsm-package.service';

@Component({
  standalone: true,
  selector: 'dsm-aps-addendum-product-preview',
  imports: [
    AccordionModule,
    ConfirmDialogModule,
    ToggleSwitchModule,
    FormsModule,
    ReactiveFormsModule,
    CurrencyFormatPipe,
    MenuModule,
    ButtonModule,
    TagModule,
    ImagePreview
  ],
  providers: [ConfirmationService],
  templateUrl: './dsm-aps-addendum-product-preview.component.html'
})
export class DsmApsAddendumProductPreviewComponent {
  private confirmationService = inject(ConfirmationService);
  private dealOptionsService = inject(DsmDealOptionsService);
  private destroyRef = inject(DestroyRef);
  private dealContext = inject(DsmApsDealContextService);
  private abilityService = inject(AbilityService);
  private lotIncentiveService = inject(DsmLotIncentiveService);
  private packageService = inject(DsmPackageService);

  actionMenu = viewChild<Menu>('actionMenu');
  actionMenuItems = signal<MenuItem[]>([]);

  addendum = input.required<DsmDealOption>();
  product = input.required<DsmDealOptionProduct>();
  isKitLinkedProduct = input<boolean>(false);
  /** Room Replacement Rules: this line still points at a room a rule replaced. Review List only. */
  isMismatchedLocation = input<boolean>(false);
  /** Room Replacement Rules: this product is the trigger of a rule that has run on the deal (review triggerProductId). */
  isReplacementTriggerExecuted = input<boolean>(false);
  isReadonly = input<boolean>(false);
  /** Set by the parent when this card's product is the one currently open in the details panel. */
  isEditing = input<boolean>(false);

  onChanges = output<void>();
  readonly isLocked = computed(() => this.addendum().isLocked);
  readonly canDelete = computed(() => !this.isLocked());
  readonly canCancel = computed(
    () =>
      this.isLocked() &&
      !this.product().isCancelled &&
      this.abilityService.ability.can('dsm-cancel-locked-addendum', 'aps')
  );

  readonly isFloorPlanTrigger = computed(() => !!this.product().roomReplacementRuleExists);
  // Trigger option whose rule hasn't run yet → "Floor Plan Added".
  readonly showFloorPlanAddedBadge = computed(() => this.isFloorPlanTrigger() && !this.isReplacementTriggerExecuted());
  // Trigger option whose rule has run → "Floor Plan Active". Only the trigger itself is badged: a product
  // whose location was introduced by a rule is still an ordinary product and keeps its own image and type.
  readonly showFloorPlanActiveBadge = computed(() => this.isFloorPlanTrigger() && this.isReplacementTriggerExecuted());

  readonly hasDiscount = computed(() => (this.product().discount ?? 0) > 0);
  readonly discountedUnitPrice = computed(() =>
    this.packageService.amountAfterDiscount(this.product().unitPrice, this.product().discountPercentage)
  );
  // discountPercentage is always the effective percent, whether the discount was set as a flat
  // amount or a percentage — rounded to 1 decimal, dropped when it's a whole number.
  readonly discountBadgeLabel = computed(() => {
    const percentage = this.product().discountPercentage ?? 0;
    const rounded = Math.round(percentage * 10) / 10;
    return `${rounded}% Discount Applied`;
  });

  readonly quantityUpdating = signal(false);
  readonly canChangeQuantity = computed(() => !this.isReadonly() && !this.isLocked() && !this.product().isCancelled);
  readonly canDecreaseQuantity = computed(
    () => this.canChangeQuantity() && !this.quantityUpdating() && this.product().quantity > 1
  );
  readonly canIncreaseQuantity = computed(() => this.canChangeQuantity() && !this.quantityUpdating());

  readonly productOptionName = computed(() => this.product().salesDescription?.trim() || this.product().productCode);
  readonly productOptionTitle = computed(() => this.product().productDescription?.trim());

  readonly categoryLine = computed(() => {
    const product = this.product();
    return [product.categoryName, product.subcategoryName].filter(Boolean).join(' / ');
  });

  readonly locationLabel = computed(() => {
    const product = this.product();
    return product.locationName?.trim() || product.locationCode?.trim() || '';
  });

  readonly attributeSummary = computed(() =>
    (this.product().attributes ?? [])
      .flatMap(attribute => (attribute.subCategoryAttributeValues ?? []).map(value => value.subCategoryAttributeValue))
      .filter(Boolean)
      .join(' · ')
  );
  readonly productImageUrl = computed(() => {
    const imgs = this.product().productImages;
    const url = imgs && imgs.length > 0 ? imgs[0]?.document?.directUrl : null;
    return url || 'assets/images/img-placeholder.png';
  });
  readonly productPreviewImages = computed((): ImagePreviewItem[] =>
    (this.product().productImages ?? []).map(image => ({
      itemImageSrc: image.document.directUrl,
      thumbnailImageSrc: image.document.directUrl,
      alt: this.product().productCode
    }))
  );

  openActionMenu(event: Event): void {
    const items: MenuItem[] = [];
    if (this.canCancel()) {
      items.push({
        label: 'Cancel Product',
        linkClass: 'text-red-600',
        command: () => this.showConfirmation('cancel')
      });
    }
    if (this.canDelete()) {
      items.push({
        label: 'Delete',
        linkClass: 'text-red-600',
        command: () => this.showConfirmation('delete')
      });
    }
    this.actionMenuItems.set(items);
    this.actionMenu()?.toggle(event);
  }

  showConfirmation(action: 'cancel' | 'delete') {
    const isDelete = action === 'delete';
    this.confirmationService.confirm({
      header: isDelete ? 'Delete Product?' : 'Cancel Product?',
      message: isDelete
        ? 'Are you sure you want to delete the Product?'
        : 'Do you want to cancel this product? This action is permanent and cannot be undone.',
      acceptLabel: isDelete ? 'Delete' : 'Cancel Product',
      rejectLabel: 'No',
      acceptButtonProps: { severity: 'danger' },
      rejectButtonProps: {
        severity: 'secondary',
        outlined: false
      },
      accept: () => {
        isDelete ? this.deleteProduct() : this.cancelProduct();
      }
    });
  }

  deleteProduct() {
    this.dealOptionsService
      .deleteAddendumProduct(this.product().id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.lotIncentiveService.reloadLotIncentive();
          this.onChanges.emit();
        },
        error: error => {
          console.error('error deleting product', error);
        }
      });
  }

  cancelProduct(): void {
    this.dealOptionsService
      .cancelAddendumProduct(this.product().id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.lotIncentiveService.reloadLotIncentive();
          this.dealContext.reloadDeal.set(true);
          this.onChanges.emit();
        }
      });
  }

  increaseQuantity(): void {
    if (!this.canIncreaseQuantity()) return;
    this.updateQuantity(this.product().quantity + 1);
  }

  decreaseQuantity(): void {
    if (!this.canDecreaseQuantity()) return;
    this.updateQuantity(this.product().quantity - 1);
  }

  // The card list (GET /addendums?...) doesn't carry modelElevationProductsId — only the
  // single-product endpoint resolves it (same one the Basic Details form relies on) — so it's
  // fetched fresh right before the update instead of trusted off `product()`. Everything else on
  // the record rides along unchanged; only quantity is different.
  private updateQuantity(quantity: number): void {
    const productId = this.product().id;

    this.quantityUpdating.set(true);
    this.dealOptionsService
      .getAddendumOptionDetailsById(productId)
      .pipe(
        switchMap(details => {
          const payload: DsmAddendumOptionsPayload = {
            modelElevationProductsId: details.modelElevationProductsId,
            selectedLocationId: details.selectedLocationId ?? null,
            unitPrice: details.unitPrice,
            notes: details.notes,
            quantity,
            attributes: (details.attributes ?? []).flatMap(attribute =>
              (attribute.subCategoryAttributeValues ?? []).map(value => ({
                subCategoryAttributeId: attribute.subCategoryAttributeId,
                subCategoryAttributeValueId: value.subCategoryAttributeValueId
              }))
            )
          };
          return this.dealOptionsService.updateAddendumOption(productId, payload, false);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.quantityUpdating.set(false);
          this.lotIncentiveService.reloadLotIncentive();
          this.onChanges.emit();
        },
        error: () => {
          this.quantityUpdating.set(false);
        }
      });
  }
}
